
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface NotificationOptions {
  title: string;
  message: string;
  eventType: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  metadata?: Record<string, any>;
  showToast?: boolean;
}

export function useAdminNotifications() {
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Function to send an admin notification
  const sendNotification = async ({
    title,
    message,
    eventType,
    relatedEntityType,
    relatedEntityId,
    metadata,
    showToast = true
  }: NotificationOptions) => {
    setIsSending(true);
    setError(null);

    try {
      // Call the Edge Function to send email and store notification
      const { error: functionError } = await supabase.functions.invoke('send-admin-email', {
        body: {
          title,
          message,
          eventType,
          relatedEntityType,
          relatedEntityId,
          metadata
        }
      });

      if (functionError) {
        throw new Error(`Failed to send notification: ${functionError.message}`);
      }

      if (showToast) {
        toast({
          title: 'Notification Sent',
          description: 'Admin notification has been sent successfully',
        });
      }

      return { success: true };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to send admin notification';
      setError(err);
      
      if (showToast) {
        toast({
          variant: 'destructive',
          title: 'Notification Failed',
          description: errorMessage,
        });
      }
      
      return { 
        success: false,
        error: errorMessage
      };
    } finally {
      setIsSending(false);
    }
  };

  // Convenience functions for different notification types
  const sendCertificateNotification = (title: string, message: string, metadata?: Record<string, any>) => {
    return sendNotification({
      title,
      message, 
      eventType: 'certificate_event',
      relatedEntityType: 'certificates',
      metadata
    });
  };

  const sendRetentionNotification = (title: string, message: string, metadata?: Record<string, any>) => {
    return sendNotification({
      title,
      message,
      eventType: 'retention_event',
      relatedEntityType: 'retention_policies',
      metadata
    });
  };

  const sendComplianceNotification = (title: string, message: string, metadata?: Record<string, any>) => {
    return sendNotification({
      title,
      message,
      eventType: 'compliance_event',
      relatedEntityType: 'certificate_reports',
      metadata
    });
  };

  const sendUrgentNotification = (title: string, message: string, metadata?: Record<string, any>) => {
    return sendNotification({
      title,
      message,
      eventType: 'urgent_alert',
      metadata
    });
  };

  return {
    sendNotification,
    sendCertificateNotification,
    sendRetentionNotification,
    sendComplianceNotification,
    sendUrgentNotification,
    isSending,
    error
  };
}
